import { resolveIncompleteSignupDestination } from '@/features/signup/helpers/resolve-incomplete-signup-destination';
import { INCOMPLETE_SIGNUP_DESTINATION_REASON } from '@/features/signup/types/resolve-incomplete-signup-destination-enums';
import type { IncompleteSignupDestination } from '@/features/signup/types/resolve-incomplete-signup-destination-types';
import { getResumeRoute } from '@/features/signup/helpers/get-resume-route';
import { resolvePostAuthDestination } from '@/lib/navigation/post-auth-destination';
import { findEffectivelyActiveAccount, isAccountEffectivelyActive } from '@/lib/helpers/account/account';
import { useAccountActions, useAccountState } from '@/contexts/account/account-context';
import { useAuthToken } from '@/contexts/auth/auth-context';
import { AccountStorage } from '@/features/workspace/api/account-storage/account-storage';
import { useSignupFlowStore } from '@/features/signup/stores/use-signup-flow-store';

function applyIncompleteDestinationStep(
  incompleteDestination: IncompleteSignupDestination
): void {
  if (incompleteDestination.completedStepToSet !== undefined) {
    useSignupFlowStore
      .getState()
      .setCompletedStep(incompleteDestination.completedStepToSet);
  }
}

export function useTokenRedirect(searchParams: URLSearchParams): string | null {
  const token = useAuthToken();
  const { setCurrentAccount } = useAccountActions();
  const { accountsList, currentAccount, hasLoadedAccounts } = useAccountState();
  const planId = useSignupFlowStore(s => s.planId);

  if (!token) return null;

  // Wait for fresh account data before making redirect decisions
  if (!hasLoadedAccounts) return null;

  const returnUrl = searchParams.get('returnUrl');
  if (returnUrl?.startsWith('/signup/')) return returnUrl;

  const { completedStep } = useSignupFlowStore.getState();
  if (completedStep > 0 && completedStep < 14) {
    return getResumeRoute(completedStep);
  }

  if (!accountsList || accountsList.length === 0) return null;

  const lastAccountId = AccountStorage.getLastAccountId();
  const preferredAccount =
    (currentAccount && accountsList.find(account => account.id === currentAccount.id)) ||
    (lastAccountId ? accountsList.find(account => account.id === lastAccountId) : undefined);

  // Prefer last-used / currentAccount even when incomplete or cancelled.
  if (preferredAccount) {
    if (!currentAccount || currentAccount.id !== preferredAccount.id) {
      setCurrentAccount(preferredAccount);
    }

    if (isAccountEffectivelyActive(preferredAccount)) {
      return resolvePostAuthDestination(searchParams.get('redirect'));
    }

    const incompleteDestination = resolveIncompleteSignupDestination(accountsList, {
      userId: '',
      completedStep: useSignupFlowStore.getState().completedStep,
      planId: planId ?? undefined,
      returnUrl: searchParams.get('returnUrl') ?? undefined,
      selectedAccountId: preferredAccount.id,
    });
    applyIncompleteDestinationStep(incompleteDestination);

    if (
      incompleteDestination.reason === INCOMPLETE_SIGNUP_DESTINATION_REASON.CANCELLED_SUBSCRIPTION
    ) {
      return searchParams.get('redirect') || incompleteDestination.redirectTo;
    }

    return incompleteDestination.redirectTo;
  }

  const activeAccount = findEffectivelyActiveAccount(accountsList);
  if (activeAccount) {
    if (!currentAccount || currentAccount.id !== activeAccount.id) {
      setCurrentAccount(activeAccount);
    }
    return resolvePostAuthDestination(searchParams.get('redirect'));
  }

  const incompleteDestination = resolveIncompleteSignupDestination(accountsList, {
    userId: '',
    completedStep: useSignupFlowStore.getState().completedStep,
    planId: planId ?? undefined,
    returnUrl: searchParams.get('returnUrl') ?? undefined,
  });
  applyIncompleteDestinationStep(incompleteDestination);

  if (incompleteDestination.account) {
    if (!currentAccount || currentAccount.id !== incompleteDestination.account.id) {
      setCurrentAccount(incompleteDestination.account);
    }
  }

  return searchParams.get('redirect') || incompleteDestination.redirectTo;
}
